import React, { useCallback, useContext, useState } from 'react';
import { CloseCircleFilled } from '@ant-design/icons';
import { Button, Form, Input, Modal } from 'antd';

import { ControlSystemDetails } from '@totem/components/controlSystemDetail/container/types';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';
import { CONTROL_SYSTEMS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import DetailSpinner from '../DetailSpinner';

import './deleteControlSystemModal.css';

const FormItem = Form.Item;

const styles = {
  form: {
    width: '100%',
  },
  errorIcon: {
    color: colors.utility.error,
  },
  details: {
    fontSize: '1.2rem',
  },
};

export interface Props {
  open: boolean;
  setOpen: (data: boolean) => void;
  controlSystem: ControlSystemDetails;
  loading?: boolean;
  onComplete?: Function;
}

const DeleteControlSystemModal = ({
  open,
  setOpen,
  controlSystem,
  loading,
  onComplete,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { doRefresh } = useContext(OrganizationContext);
  const [confirm, setConfirm] = useState<string>('');
  const [isSending, setIsSending] = useState(false);

  const sendControlSystemDelete = useCallback(
    async (id: string) => {
      if (!isSending) {
        setIsSending(true);

        fetch(`${CONTROL_SYSTEMS_ENDPOINT}/${id}`, {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        }).then((res) => {
          setConfirm('');

          setIsSending(false);

          CheckResponseShowError(res);

          setOpen(false);
          if (onComplete) {
            onComplete();
          }
          doRefresh();
        });
      }
    },
    [getApiToken],
  );

  const onCancel = () => {
    setOpen(false);

    setConfirm('');
  };

  const onOk = async () => {
    if (isNotNull(controlSystem)) {
      sendControlSystemDelete(controlSystem.controlSystem.id);
    }
  };

  const isDeleteDisabled = confirm !== 'DELETE';

  return (
    <Modal
      title={
        <div>
          <CloseCircleFilled style={styles.errorIcon} />
          <span styleName="title">Delete Control System</span>
        </div>
      }
      open={open}
      footer={
        <div styleName="footer">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            danger
            loading={isSending}
            disabled={isDeleteDisabled}
            onClick={onOk}
          >
            Yes, Delete
          </Button>
        </div>
      }
      onCancel={onCancel}
      /*
       * used for the purposes of unmounting the AssignedContentCollapse
       * component to render collapse panel inactive onClose
       */
      destroyOnHidden
    >
      {loading ? (
        <div styleName="spinner-container">
          <DetailSpinner color={colors.utility.error} />
        </div>
      ) : (
        <>
          <div styleName="description">
            Deleting this control system will remove all audit responses,
            backups, and notes attached to the system.
          </div>
          <div styleName="collapse-title">
            This content will be removed with the control system:
          </div>
          <Form layout="vertical" style={styles.form}>
            <FormItem
              label={
                <span>
                  Type <strong>"DELETE"</strong> to confirm
                </span>
              }
              colon={false}
            >
              <Input
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </FormItem>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default DeleteControlSystemModal;
